import { useEffect, useState } from 'react';
import { C, SERIF } from '../../theme';
import { Icon, Mark } from '../../icons';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../api';
import type { Me, Property } from '../../api';
import { InstallCard } from '../../components/InstallCard';

/** One editable field in a card. */
interface FieldSpec {
  key: string;
  label: string;
  value: string;
  required?: boolean;
  placeholder?: string;
  inputMode?: 'text' | 'email' | 'tel';
}

/**
 * Settings — the property record and the signed-in owner's profile, both
 * editable in place, plus an About line and log-out. Saving a card PATCHes the
 * backend and lifts the fresh values up so the sidebar and scope chip update.
 */
export function SettingsPage({
  me,
  property,
  onLogout,
  onMeChange,
  onPropertyChange,
}: {
  me: Me;
  property?: Property | null;
  onLogout: () => void;
  onMeChange?: (me: Me) => void;
  onPropertyChange?: (p: Property) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ fontFamily: SERIF, fontSize: 22, margin: '0 0 3px', fontWeight: 600 }}>Settings</h2>
        <div style={{ color: C.muted, fontSize: 13.5 }}>Your property, profile and account</div>
      </div>

      {/* property */}
      {property ? (
        <EditableCard
          icon="pin"
          title="Property"
          fields={[
            { key: 'name', label: 'Name', value: property.name, required: true, placeholder: 'e.g. Sunrise Commercial Complex' },
            { key: 'address', label: 'Address', value: property.address ?? '', placeholder: 'Street / building' },
            { key: 'city', label: 'City', value: property.city ?? '', placeholder: 'e.g. Margao' },
            { key: 'state', label: 'State', value: property.state ?? '', placeholder: 'e.g. Goa' },
          ]}
          staticRows={property.unitCount != null ? [{ label: 'Units', value: String(property.unitCount) }] : []}
          onSave={async (v) => {
            const updated = await api.updateProperty(property.id, {
              name: v.name,
              address: v.address || null,
              city: v.city || null,
              state: v.state || null,
            });
            onPropertyChange?.(updated);
          }}
        />
      ) : (
        <Card icon="pin" title="Property">
          <div style={{ color: C.muted, fontSize: 13.5, padding: '4px 0' }}>No property linked to this account yet.</div>
        </Card>
      )}

      {/* profile */}
      <EditableCard
        icon="key"
        title="Your profile"
        fields={[
          { key: 'displayName', label: 'Name', value: me.displayName ?? '', required: true, placeholder: 'Your name' },
          { key: 'email', label: 'Email', value: me.email ?? '', placeholder: 'you@example.com', inputMode: 'email' },
          { key: 'phone', label: 'Phone', value: me.phone ?? '', placeholder: '+91…', inputMode: 'tel' },
        ]}
        staticRows={[{ label: 'Role', value: me.role.charAt(0).toUpperCase() + me.role.slice(1) }]}
        onSave={async (v) => {
          const updated = await api.updateProfile({
            displayName: v.displayName,
            email: v.email || null,
            phone: v.phone || null,
          });
          onMeChange?.(updated);
        }}
      />

      <InstallCard />

      <SampleDataCard />

      {/* about + logout */}
      <Card icon="grid" title="About Premise">
        <div style={{ fontSize: 13.5, color: C.ink2, lineHeight: 1.55, padding: '4px 0' }}>
          Premise is your commercial-tenancy record of truth: rent, documents and dates in one calm place.
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 6 }}>Version 0.1.0</div>
      </Card>

      <button onClick={onLogout} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, alignSelf: 'flex-start', background: C.card, color: C.red, border: `1px solid ${C.line}`, borderRadius: 10, padding: '11px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
        <Icon n="logout" size={17} /> Log out
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.muted, fontSize: 12, opacity: 0.8, marginTop: 2 }}>
        <Mark size={18} /> Premise · Aperture
      </div>
    </div>
  );
}

/** A card whose fields flip between read-only rows and inputs, with Save/Cancel. */
function EditableCard({
  icon,
  title,
  fields,
  staticRows = [],
  onSave,
}: {
  icon: string;
  title: string;
  fields: FieldSpec[];
  staticRows?: Array<{ label: string; value: string }>;
  onSave: (values: Record<string, string>) => Promise<void>;
}) {
  const notify = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function start() {
    setDraft(Object.fromEntries(fields.map((f) => [f.key, f.value])));
    setError(null);
    setEditing(true);
  }
  function cancel() {
    setEditing(false);
    setError(null);
  }
  async function save() {
    const missing = fields.find((f) => f.required && !(draft[f.key] ?? '').trim());
    if (missing) {
      setError(`${missing.label} can’t be empty.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(Object.fromEntries(fields.map((f) => [f.key, (draft[f.key] ?? '').trim()])));
      notify(`${title} saved`);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 16px', borderBottom: `1px solid ${C.line2}` }}>
        <span style={{ width: 30, height: 30, borderRadius: 8, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
          <Icon n={icon} size={16} />
        </span>
        <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 650, color: C.ink, flex: 1 }}>{title}</h3>
        {!editing && (
          <button onClick={start} style={linkBtn}>
            <Icon n="gear" size={13} /> Edit
          </button>
        )}
      </div>

      <div style={{ padding: '6px 16px 14px' }}>
        {editing ? (
          <>
            {error && (
              <div style={{ background: C.redSoft, color: C.red, fontSize: 12.6, fontWeight: 500, padding: '9px 11px', borderRadius: 9, margin: '8px 0 4px' }}>{error}</div>
            )}
            {fields.map((f) => (
              <label key={f.key} style={{ display: 'block', padding: '8px 0' }}>
                <span style={{ fontSize: 11.5, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', marginBottom: 5 }}>
                  {f.label}{f.required ? '' : ' (optional)'}
                </span>
                <input
                  value={draft[f.key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  inputMode={f.inputMode}
                  style={input}
                />
              </label>
            ))}
            {staticRows.map((r) => (
              <Field key={r.label} label={r.label} value={r.value} muted />
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button onClick={cancel} disabled={busy} style={ghostBtn}>Cancel</button>
              <button onClick={save} disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </>
        ) : (
          <>
            {fields.map((f) => (
              <Field key={f.key} label={f.label} value={f.value || '–'} />
            ))}
            {staticRows.map((r) => (
              <Field key={r.label} label={r.label} value={r.value} muted />
            ))}
          </>
        )}
      </div>
    </div>
  );
}


/**
 * Sample data — load or remove a small, clearly-flagged demo set so a new owner
 * can see how rooms, tenancies, rent and payments fit together. Every row it
 * writes carries an is-sample flag, so removing it can never touch real records.
 */
function SampleDataCard() {
  const notify = useToast();
  const [present, setPresent] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const s = await api.sampleStatus();
      setPresent(s.present);
    } catch {
      setPresent(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  async function act(load: boolean) {
    setBusy(true);
    try {
      if (load) {
        await api.loadSampleData();
        notify('Sample data added. Look for the Example badge.');
      } else {
        await api.removeSampleData();
        notify('Sample data removed. Your own records are untouched.');
      }
      await refresh();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not update sample data. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card icon="grid" title="Sample data">
      <div style={{ fontSize: 13.3, color: C.ink2, lineHeight: 1.55, padding: '4px 0 10px' }}>
        A small example set (one shop on a commercial lease with TDS, one room let to an
        individual) that shows how the pieces fit together. Every example row is marked{' '}
        <b style={{ color: C.ink }}>Example</b> and can be removed in one click. Your own
        records are never affected.
      </div>
      <button
        onClick={() => act(!present)}
        disabled={busy || present === null}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: present ? C.card : C.accent,
          color: present ? C.ink2 : '#fff',
          border: present ? `1px solid ${C.line}` : 0,
          borderRadius: 10, padding: '10px 16px', fontWeight: 600, fontSize: 13.5,
          cursor: busy || present === null ? 'default' : 'pointer',
          opacity: busy || present === null ? 0.6 : 1,
        }}
      >
        <Icon n={present ? 'trash' : 'plus'} size={15} color={present ? C.red : '#fff'} />
        {present === null ? 'Checking…' : present ? 'Remove sample data' : 'Load sample data'}
      </button>
    </Card>
  );
}

function Card({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 16px', borderBottom: `1px solid ${C.line2}` }}>
        <span style={{ width: 30, height: 30, borderRadius: 8, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
          <Icon n={icon} size={16} />
        </span>
        <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 650, color: C.ink }}>{title}</h3>
      </div>
      <div style={{ padding: '6px 16px 14px' }}>{children}</div>
    </div>
  );
}

function Field({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, padding: '9px 0', borderBottom: `1px solid ${C.line2}` }}>
      <span style={{ fontSize: 12.5, color: C.muted, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13.8, color: muted ? C.muted : C.ink, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

const linkBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent', color: C.accent, border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 11px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' };
const primaryBtn: React.CSSProperties = { background: C.accent, color: '#fff', border: 0, borderRadius: 9, padding: '9px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const ghostBtn: React.CSSProperties = { background: C.card, color: C.ink2, border: `1px solid ${C.line}`, borderRadius: 9, padding: '9px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const input: React.CSSProperties = { width: '100%', border: `1px solid ${C.line}`, borderRadius: 9, padding: '9px 11px', fontSize: 14, color: C.ink, background: '#fff', fontFamily: 'inherit', outline: 'none' };
